import _ from "lodash";
import { connect, useSelector } from "react-redux";
import { Form } from "formik";
import React, { useEffect, useState } from "react";
import Recaptcha from "react-recaptcha";
import * as Yup from "yup";

import { adultDobValidationSchemaFactory, yup_password_factory } from "@/utils/yupSchemaUtils";
import { Button2 } from "@/ui2/atoms/Button";
import { ConsentDisclaimer } from "@/components/Consent/ConsentDisclaimer";
import { ConsentEmail, ConsentSMS, CustomForm } from "@/component-library";
import { ConsentToTreatment } from "@/components/Consent/ConsentToTreatment";
import { createAcknowledgement } from "@/utils/acknowledgementUtils";
import { createImplicitConsent } from "@/utils/consentUtils";
import FormikInput from "@/components/UI/FormikInput";
import FormikInputFormat from "@/components/UI/formikMobileInput";
import { getAutomationDataAttr } from "@/utils/automationUtils";
import { getCurrentPolicyVersion } from "@/lib/helpers/getCurrentPolicyVersion";
import images from "@/utils/images";
import InputError from "@/components/UI/InputError";
import InterestInGuardianship, {
  GUARDIANSHIP_PROGRAM_TYPE,
} from "@/components/Common/InterestInGuardianship";
import { Link2 } from "@/ui2/atoms/Link";
import { OPERATIONAL_OVERRIDE_RECAPTCHA } from "@/constants/featureFlags";
import publicRequests from "@/APIRequests/public";

const PrimaryAccountInfo = (props) => {
  const { accountData, nextStep, setAccountData } = props;

  let reCaptchaRef = React.createRef();

  const [mounted, setMounted] = useState(false);
  const [recaptchaVerified, setRecaptchaVerified] = useState(false);
  const [recaptchaError, setRecaptchaError] = useState(null);
  const [interestedInGuardianship, setInterestedInGuardianship] = useState(false);
  const [consent, setConsent] = useState({
    email: null,
    sms: null,
    treatment: null,
  });
  const [policyMetadata, setPolicyMetadata] = useState({});
  const publicFlags = useSelector((state) => state.flags.publicFlags);
  const publicFlagsHaveLoaded = useSelector((state) => state.flags.publicFlagsHaveLoaded);
  const policyVersion = getCurrentPolicyVersion({
    policy: "CONSENT_TO_TREATMENT",
    policyMetadata,
  });

  useEffect(() => {
    (async () => {
      setPolicyMetadata(await publicRequests.get_policy_metadata());
    })();
  }, []);

  useEffect(() => {
    setTimeout(() => {
      setMounted(true);
    }, 500);

    if (publicFlags?.[OPERATIONAL_OVERRIDE_RECAPTCHA]) {
      setRecaptchaVerified(true);
    }
  }, [publicFlagsHaveLoaded]);

  const submitHandler = async (formData) => {
    if (!recaptchaVerified) {
      setRecaptchaError("reCAPTCHA is Required");

      return;
    }

    formData.interested_in_guardianship = interestedInGuardianship;

    if (interestedInGuardianship && !formData.guardianship_program_type) {
      formData.guardianship_program_type = GUARDIANSHIP_PROGRAM_TYPE.DEFAULT;
    }

    const implicitConsents = await createImplicitConsent(policyMetadata);

    formData["consent"] = { ...implicitConsents, ...consent };

    setAccountData(formData);
    nextStep();
  };

  const consentHandler = async (policyName, policyVersion) => {
    let currConsent = _.cloneDeep(consent);
    const newConsent = {
      ...(await createAcknowledgement({ consentResponse: true })),
      version: policyVersion,
    };

    if (consent[policyName] == null) {
      currConsent[policyName] = [newConsent];
      setConsent(currConsent);
    } else {
      currConsent[policyName] = null;
      setConsent(currConsent);
    }
  };

  const renderForm = (formikProps) => {
    const formikInputProps = {
      errors: formikProps.errors,
      inputClasses: "Input--sm",
      touched: formikProps.touched,
    };

    return (
      <Form id="selfsignupformik">
        <div className="PrimaryAccountInfo--form-container">
          <FormikInput
            {...formikInputProps}
            formEl={{
              elementConfig: {
                label: "First Name",
                labelClasses: "fs-16",
                placeholder: "Enter First Name",
                type: "text",
              },
              elementType: "input",
              name: "first_name",
            }}
            testId="self-sign-up-first-name"
          />

          <FormikInput
            {...formikInputProps}
            formEl={{
              elementConfig: {
                label: "Last Name",
                labelClasses: "fs-16",
                placeholder: "Enter Last Name",
                type: "text",
              },
              elementType: "input",
              name: "last_name",
            }}
            testId="self-sign-up-last-name"
          />

          <FormikInputFormat
            {...formikInputProps}
            formEl={{
              elementConfig: {
                label: "Date of Birth",
                labelClasses: "fs-16",
                placeholder: "MM/DD/YYYY",
                type: "text",
              },
              elementType: "input",
              name: "dob",
            }}
            onChange={async (val) => {
              await formikProps.setFieldValue("dob", val.value);
              formikProps.setFieldTouched("dob");
            }}
            testId="self-sign-up-dob"
            value={formikProps && formikProps.values["dob"]}
          />

          <FormikInputFormat
            {...formikInputProps}
            formEl={{
              elementConfig: {
                label: "Mobile",
                labelClasses: "fs-16",
                placeholder: "Enter Mobile",
                type: "text",
              },
              elementType: "input",
              name: "mobile",
            }}
            onChange={async (val) => {
              await formikProps.setFieldValue("mobile", val.value);
              formikProps.setFieldTouched("mobile");
            }}
            testId="self-sign-up-mobile"
            value={formikProps && formikProps.values["mobile"]}
          />

          <FormikInput
            {...formikInputProps}
            formEl={{
              elementConfig: {
                label: "Email",
                labelClasses: "fs-16",
                placeholder: "Enter Email",
                type: "email",
              },
              elementType: "input",
              name: "email",
            }}
            testId="self-sign-up-email"
          />

          <FormikInput
            {...formikInputProps}
            formEl={{
              elementConfig: {
                label: "Create Password",
                labelClasses: "fs-16",
                placeholder: "Enter Password",
                type: "viewablePassword",
              },
              elementType: "input",
              name: "password",
            }}
            testId="self-sign-up-password"
          />

          <FormikInput
            {...formikInputProps}
            formEl={{
              elementConfig: {
                label: "Confirm Password",
                labelClasses: "fs-16",
                placeholder: "Re-enter Password",
                type: "viewablePassword",
              },
              elementType: "input",
              name: "confirm_password",
            }}
            testId="self-sign-up-confirm-password"
          />

          <div className="my-3 d-flex fs-15">
            <InterestInGuardianship
              interestedInGuardianship={interestedInGuardianship}
              setInterestedInGuardianship={() =>
                setInterestedInGuardianship(!interestedInGuardianship)
              }
              testId="self-sign-up-interested-in-guardianship"
            />
          </div>
        </div>
      </Form>
    );
  };

  return (
    <>
      <div className="p-5">
        <div className="fs-24 text-center tw:font-bold">Welcome to aptihealth!</div>
        <div className="fs-18 text-center txt-gry">Let’s get started by creating your account</div>

        <div className="text-center mt-3">
          <span>Already have an account?</span>
          <Link2 className="tw:ml-ch" href="/auth/sign-in">
            <b>Log In</b>
          </Link2>
        </div>

        <div className="text-center my-4">
          <img
            alt="Step 1/3"
            className="signup-progress-bar"
            src={images("./signup/progress-1.png")}
          />
        </div>

        <CustomForm
          initialValues={accountData}
          onSubmit={submitHandler}
          render={renderForm}
          validationSchema={accountPart1Validation()}
        />

        <div>
          <ConsentToTreatment
            className="tw:my-0 tw:bg-fill-white tw:pb-0 tw:pl-0 tw:[&>p]:text-sm!"
            consentEvent={() => consentHandler("treatment", policyVersion)}
            version={policyVersion}
          />
        </div>

        <div className="tw:my-3 tw:text-sm tw:text-text-tertiary">
          <span id="self-signup-consent">
            <ConsentEmail consentEvent={() => consentHandler("email")} />
          </span>
        </div>

        <div className="tw:my-3 tw:text-sm tw:text-text-tertiary">
          <span id="self-signup-consent">
            <ConsentSMS consentEvent={() => consentHandler("sms")} />
          </span>
        </div>

        <div className="mt-5 mb-4 text-center w-100">
          <div>
            {mounted && (
              <Recaptcha
                className="mx-auto recaptcha"
                ref={reCaptchaRef}
                sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY}
                size={window.innerWidth < 768 ? "compact" : "normal"}
                verifyCallback={() => {
                  setRecaptchaError(null);
                  setRecaptchaVerified(true);
                }}
              />
            )}
          </div>

          <div className="text-center my-2">
            {recaptchaError && (
              <InputError style={{ position: "relative" }}>{recaptchaError}</InputError>
            )}
          </div>
        </div>

        <div className="d-flex tw:justify-center">
          <Button2
            form="selfsignupformik"
            isDisabled={consent["treatment"] ? null : "disabled"}
            size="xl"
            type="submit"
            {...getAutomationDataAttr("self-sign-up-primary-info-button")}
          >
            Agree & Continue
          </Button2>
        </div>

        <ConsentDisclaimer className="tw:mt-8!" policyMetadata={policyMetadata} />
      </div>
    </>
  );
};

const mapStateToProps = (state) => {
  return {
    configs: state.configs,
  };
};

export default connect(mapStateToProps, {})(PrimaryAccountInfo);

const accountPart1Validation = () =>
  Yup.object().shape({
    confirm_password: Yup.string()
      .required("Confirm password is required")
      .oneOf([Yup.ref("password"), null], "Password does not match"),
    dob: adultDobValidationSchemaFactory(
      "MMDDYYYY",
      "Must be a valid date",
      "To register a child for care aged 5-17, you must first register yourself. " +
        "Toggle the button below and you will be prompted to register your child when you finish.",
    ).required("Date of birth is required"),
    email: Yup.string().email("Enter a valid email address").required("Email is required"),
    first_name: Yup.string()
      .required("First name is required")
      .matches(/^[a-zA-Z-]+$/, "Numerals, spaces & special characters are not allowed"),
    last_name: Yup.string()
      .required("Last name is required")
      .matches(/^[a-zA-Z-]+$/, "Numerals, spaces & special characters are not allowed"),
    mobile: Yup.string()
      .matches(/^\d{10}$/, "Contact number must be a valid US number")
      .required("Mobile number is required"),
    password: yup_password_factory(),
  });
